// Guest event identity: one validated, generation-stamped scope per event visit.
//
// Only EventRouteGate activates/clears the scope. Everything else (pages,
// contexts, background downloads) captures `getEventScope()` before awaiting
// and re-checks `isCurrentScope(captured)` after every await and before every
// shared write (state setters, shared storage, navigation, timers).
//
// Staleness is by monotonic generation, never by name equality: A -> B -> A
// mints three scopes, so a callback captured during the first A stays stale.
// Navigating between pages of the SAME event keeps the scope (and generation),
// which is what lets the face -> body background downloads complete.
import { v4 as uuidv4 } from "uuid";

let generationCounter = 0; // never decreases or resets
let currentScope = null;
const mintedScopes = new WeakSet();

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

// Returns the same string when usable, else null. Deliberately permissive:
// the server stays the authority on which names exist.
export const validateEventName = (raw) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const lowered = trimmed.toLowerCase();
  if (lowered === "undefined" || lowered === "null") return null;
  if (trimmed === "." || trimmed === "..") return null;
  if (raw.includes("/") || raw.includes("\\")) return null;
  if (CONTROL_CHARS.test(raw)) return null;
  return raw;
};

// Frozen { name, generation } of the active event, or null.
export const getEventScope = () => currentScope;

// True only for a scope minted here that is still the active one (identity).
export const isCurrentScope = (scope) =>
  Boolean(scope) && mintedScopes.has(scope) && scope === currentScope;

// ---- Gate-only mutation ---------------------------------------------------

// Same name as the active scope keeps it (same generation); a different name
// mints a new generation.
export const activateEventScope = (validatedName) => {
  if (validateEventName(validatedName) === null) {
    throw new Error("activateEventScope requires a validated event name");
  }
  if (currentScope && currentScope.name === validatedName) {
    return currentScope;
  }
  const scope = Object.freeze({
    name: validatedName,
    generation: ++generationCounter,
  });
  mintedScopes.add(scope);
  currentScope = scope;
  return scope;
};

// With a scope: invalidates only that scope (a stale cleanup cannot clear a
// newer one). Without: clears whatever is active (invalid route).
export const clearEventScope = (scope) => {
  if (scope === undefined || scope === currentScope) {
    currentScope = null;
    activeGuestId = null;
  }
};

// ---- Guest flow storage ---------------------------------------------------

// Exact named guest-flow keys. Never storage.clear(): admin/auth keys and
// IndexedDB are outside the event flow.
export const EVENT_FLOW_STORAGE_KEYS = Object.freeze({
  session: Object.freeze([
    "event_name",
    "user_id",
    "reFace",
    "uploads_bodies",
    "number",
    "selectedFaces",
    "selectedBodies",
    "faceImageBlobUrls",
    "bodyImageBlobUrls",
    "faceImagesDownloadStatus",
    "finalImagePixelRatio",
  ]),
  local: Object.freeze(["pendingGif", "finalImage", "imageParts"]),
});

export const newGuestId = () => uuidv4();

// ---- Active guest id ------------------------------------------------------
//
// The guest id the flow runs under, kept in memory next to the React context
// value and set by the same statements (EventRouteGate, SwingieHome,
// MinimoveHome). sessionStorage["user_id"] is only the reload carrier: on
// 2026-10-18 (Production, SAGIT/NOFAR) devices whose storage had lost that key
// while the context still held the id sent face-hold acquires without a guest,
// and every approve of those guests was refused as guest_mismatch. Readers go
// through faceClaimClient.currentGuestId(), which prefers this register.
let activeGuestId = null;

export const setActiveGuestId = (guestId) => {
  activeGuestId = guestId || null;
};

export const getActiveGuestId = () => activeGuestId;

// The identity writes that must never throw into a page: storage unavailable
// means the in-memory flow still works (a reload then starts a fresh guest).
export const storeGuestIdentity = (eventName, guestId) => {
  try {
    sessionStorage.setItem("user_id", guestId);
    sessionStorage.setItem("event_name", eventName);
  } catch (error) {
    // storage unavailable or full: the register above carries the id
  }
};

export const readStoredGuestId = () => {
  try {
    return sessionStorage.getItem("user_id") || null;
  } catch (error) {
    return null;
  }
};

const pendingGifTargetsEvent = (eventName) => {
  try {
    const raw = localStorage.getItem("pendingGif");
    if (!raw) return false;
    const { navigateTo } = JSON.parse(raw);
    if (typeof navigateTo !== "string") return false;
    return decodeURIComponent(navigateTo.split("/")[1] || "") === eventName;
  } catch (error) {
    return false;
  }
};

// Removes only the named guest-flow keys. A pendingGif that targets
// `keepPendingGifFor` survives (valid same-event replay).
export const resetEventFlowStorage = (keepPendingGifFor) => {
  try {
    const urls = JSON.parse(sessionStorage.getItem("faceImageBlobUrls") || "{}");
    Object.values(urls).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  } catch (error) {
    // unreadable value: just drop it below
  }
  const keepGif =
    keepPendingGifFor !== undefined && pendingGifTargetsEvent(keepPendingGifFor);
  EVENT_FLOW_STORAGE_KEYS.session.forEach((key) => sessionStorage.removeItem(key));
  EVENT_FLOW_STORAGE_KEYS.local.forEach((key) => {
    if (key === "pendingGif" && keepGif) return;
    localStorage.removeItem(key);
  });
};
